import React, { useContext } from "react"

import { RouteComponentProps } from "react-router"
import { gql, useQuery } from "urql"

import { Color } from "~tailwindui/types/enums"

import NotFound from "~Tutor/Pages/NotFound"
import TutorContext from "~Tutor/TutorContext"
import { path } from "~Tutor/TutorRoutes"
import ProxyNumber from "~Tutor/components/ProxyNumber"
import { H3, StyledLink, WithLoadingIndicator } from "~tailwindui/Basics"
import DescriptionItem from "~tailwindui/Basics/DescriptionItem"
import H4 from "~tailwindui/Basics/H4"
import Panel from "~tailwindui/Basics/Panel"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import { MatchParams } from "src/types/MatchParams"

import useIsMobile from "hooks/useIsMobile"

import Avatar from "components/Messaging/Avatar"
import ScheduleSessionForStudentButton from "components/Tutor/components/Sessions/ScheduleSessionForStudentButton"

import ScheduledSessions from "./FutureSessions"
import PastSessions from "./PastSessions"

const StudentDetails: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const studentId = match.params.id
  const isMobile = useIsMobile()
  const { viewer } = useContext(TutorContext)
  const tutorId = viewer.id

  const [result] = useQuery({
    query,
    variables: { studentId, tutorId },
  })

  if (result.data && !result.data.student) return <NotFound />

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { student } }) => (
        <>
          <H3 className="flex items-center">
            {student.firstName}'s Information
          </H3>
          <hr className="mb-4" />

          <div className="flex flex-col gap-4 md:flex-row">
            <Panel className="grow">
              <div className="flex flex-col gap-4 sm:flex-row">
                <Avatar
                  size={isMobile ? "medium" : "large"}
                  alt={student.fullName}
                  src={student.avatar.url}
                />
                <div>
                  <DescriptionItem label="Name" value={student.fullName} />
                  <DescriptionItem
                    label="Phone Number"
                    value={
                      <ProxyNumber
                        noNumber={student.noStudentPhone}
                        proxyNumber={student.tutorToStudentPhoneNumber}
                        studentId={studentId}
                      />
                    }
                  />
                  <DescriptionItem label="Notes" value={student.notes} />
                  <DescriptionItem
                    label="Subjects"
                    value={student.subjects
                      .map(subject => subject.name)
                      .join(", ")}
                  />

                  {student.parentName && (
                    <DescriptionItem
                      label="Primary Parent Name"
                      value={student.parentName}
                    />
                  )}
                  {student.parentPhonePresent && (
                    <DescriptionItem
                      label="Primary Parent Phone"
                      value={
                        <ProxyNumber
                          noNumber={!student.parentPhonePresent}
                          proxyNumber={student.tutorToParentPhoneNumber}
                          studentId={studentId}
                        />
                      }
                    />
                  )}

                  {student.secondaryParentName && (
                    <DescriptionItem
                      label="Secondary Parent Name"
                      value={student.secondaryParentName}
                    />
                  )}
                  {student.secondaryParentPhonePresent && (
                    <DescriptionItem
                      label="Secondary Parent Phone"
                      value={
                        <ProxyNumber
                          noNumber={!student.secondaryParentPhonePresent}
                          proxyNumber={
                            student.tutorToSecondaryParentPhoneNumber
                          }
                          studentId={studentId}
                        />
                      }
                    />
                  )}
                </div>
              </div>
            </Panel>

            <Panel className="min-w-max">
              <H4 className="mb-2 text-center">Links</H4>
              <div className="flex flex-col space-y-2">
                <ScheduleSessionForStudentButton
                  student={student}
                  className="w-full"
                />
                <StyledLink
                  to={path("studentTailoredAssignmentsIndex", {
                    id: studentId,
                  })}
                  button
                  color={Color.Blue}
                >
                  View Tailored Assignments
                </StyledLink>
                <StyledLink
                  to={path("studentLessonPlansIndex", {
                    id: studentId,
                  })}
                  button
                  color={Color.Blue}
                >
                  View Lesson Plans
                </StyledLink>

                {student.hasTestPrepAccess && (
                  <StyledLink
                    to={path("studentTestPrepExamsIndex", {
                      id: student.id,
                    })}
                    className="mb-4 inline-block"
                    button
                    color={Color.Orange}
                  >
                    View Test Prep
                  </StyledLink>
                )}
                {student.organization && student.organizationOneOnOneGroup && (
                  <StyledLink
                    to={path("studyGroupDetails", {
                      id: student.organizationOneOnOneGroup.id,
                    })}
                    className={buttonClassNames({ color: Color.Orange })}
                  >
                    Go to Organization 1-1 Group
                  </StyledLink>
                )}
              </div>
            </Panel>
          </div>

          <Panel className="flex flex-col gap-4 md:flex-row">
            <div>
              <DescriptionItem label="Grade" value={student.grade?.name} />
              <DescriptionItem label="School" value={student.school?.name} />

              {student.organization && (
                <>
                  <DescriptionItem
                    label="Organization"
                    value={student.organization.name}
                  />
                  {student.organization.details?.length > 0 && (
                    <DescriptionItem
                      label="Organization Details"
                      value={student.organization.details}
                    />
                  )}
                  <DescriptionItem
                    label="Organization Time Zone"
                    value={student.organization.emailTimeZone}
                  />
                </>
              )}

              <DescriptionItem
                label="Study Groups"
                value={
                  <ul>
                    {student.studyGroups.map(group => (
                      <li key={group.id}>
                        <StyledLink
                          to={path("studyGroupDetails", { id: group.id })}
                        >
                          {group.name}
                        </StyledLink>
                      </li>
                    ))}
                  </ul>
                }
              />
            </div>
          </Panel>

          <H3 className="flex items-center">
            Your Session with {student.firstName}
          </H3>
          <hr className="mb-4" />

          <ScheduledSessions studentId={student.id} />
          <PastSessions studentId={student.id} />
        </>
      )}
    </WithLoadingIndicator>
  )
}

const query = gql`
  query StudentDetails($studentId: ID!, $tutorId: ID!) {
    student(id: $studentId) {
      id
      fullName
      firstName
      hasPrivateBillingMethod
      notes
      noStudentPhone
      tutorToStudentPhoneNumber(tutorId: $tutorId)
      parentName
      parentPhonePresent
      tutorToParentPhoneNumber(tutorId: $tutorId)
      secondaryParentName
      secondaryParentPhonePresent
      tutorToSecondaryParentPhoneNumber(tutorId: $tutorId)
      organizationOneOnOneGroup(tutorId: $tutorId) {
        id
      }
      subjects {
        id
        name
      }
      hasTestPrepAccess
      grade {
        id
        name
      }
      emailTimeZone
      organization {
        id
        name
        details
      }
      school {
        id
        name
      }
      studyGroups(tutorId: $tutorId) {
        id
        name
        oneOnOne
        tutorMaySchedule
        organization {
          id
          name
        }
      }
      avatar {
        url
      }
      avatar {
        url
      }
    }
  }
`

export default StudentDetails
